<!-- src/components/Pages/OIDCLogin.vue -->
<script setup>
import { ElMessage } from "element-plus";
import { onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import userManager from "../../services/oidcUserManager";
import globoIcon from "@/assets/img/globoIcon.png";

const route = useRoute();
const router = useRouter();

const getRedirectPath = () => {
  const redirect = route.query.redirect;
  return typeof redirect === "string" && redirect.startsWith("/") && !redirect.startsWith("//")
    ? redirect
    : "/dashboard";
};

const checkActiveSession = async () => {
  const user = await userManager.getUser();
  return !!(user && !user.expired);
};

const login = async () => {
  try {
    await userManager.signinRedirect({ state: { redirectPath: getRedirectPath() } });
  } catch (e) {
    ElMessage.error("Falha ao redirecionar para o login: " + (e?.message || e));
  }
};

onMounted(async () => {
  if (await checkActiveSession()) {
    router.replace(getRedirectPath());
  }
});
</script>

<template>
  <div class="login-page">
    <div class="glass-card" role="dialog" aria-labelledby="title" aria-describedby="subtitle">
      <div class="logo-box" aria-hidden="true">
        <img :src="globoIcon" alt="Logomarca" />
      </div>

      <h1 id="title" class="title">Datacasting</h1>
      <p id="subtitle" class="subtitle">Acesse sua conta para continuar.</p>

      <button class="ms-btn" type="button" @click="login" aria-label="Entrar com Microsoft">
        <span class="ms-icon" aria-hidden="true">
          <!-- Microsoft 4 tiles -->
          <svg viewBox="0 0 23 23" width="18" height="18">
            <rect x="1"  y="1"  width="10" height="10" fill="#F35325"/>
            <rect x="12" y="1"  width="10" height="10" fill="#81BC06"/>
            <rect x="1"  y="12" width="10" height="10" fill="#05A6F0"/>
            <rect x="12" y="12" width="10" height="10" fill="#FFBA08"/>
          </svg>
        </span>
        <span class="ms-text">Entrar com Microsoft</span>
      </button>

      <p class="hint">Se sua sessão já estiver ativa, você será redirecionado para a agenda.</p>
    </div>
  </div>
</template>

<style scoped>
/* Fundo em gradiente (como o mock) */
.login-page {
  min-height: 100dvh;
  background: linear-gradient(135deg, #2a9df4 0%, #7b2fff 100%);
  display: grid;
  place-items: center;
  padding: 24px;
}

/* Cartão central com leve glass/blur */
.glass-card {
  width: min(480px, 92vw);
  border-radius: 18px;
  padding: 32px 28px;
  background: rgba(10, 20, 45, 0.55);
  backdrop-filter: blur(8px);
  box-shadow: 0 18px 40px rgba(0,0,0,.25);
  color: #f7f9fb;
  text-align: center;
}

/* Quadrado do logo */
.logo-box {
  width: 96px;
  height: 96px;
  margin: 0 auto 14px;
  border-radius: 20px;
  background: rgba(255,255,255,.12);
  display: grid;
  place-items: center;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.08);
}
.logo-box img {
  width: 62px;
  height: 62px;
  object-fit: contain;
}

/* Título + subtítulo */
.title {
  margin: 6px 0 6px;
  font-size: 28px;
  line-height: 1.15;
  font-family: "Globo-Bold","Globo-Regular", system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
}
.subtitle {
  margin: 0 0 18px;
  color: #d7dbe0;
  font-size: 14px;
}

/* Botão Microsoft */
.ms-btn {
  width: 100%;
  height: 44px;
  border: 0;
  border-radius: 10px;
  background: #185abc; /* azul MS */
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: transform .04s ease, box-shadow .2s ease, background-color .2s ease;
  box-shadow: 0 10px 24px rgba(0,0,0,.2);
}
.ms-btn:hover { background: #1b66d1; }
.ms-btn:active { transform: translateY(1px); }

.ms-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ms-text {
  white-space: nowrap;
}

/* Dica abaixo do botão */
.hint {
  margin-top: 14px;
  font-size: 12px;
  color: #d0d6de;
}
</style>
